import { useCallback, useMemo, type CSSProperties } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import type { AppDefinition } from '@clarix/shared-types';
import { cn } from '@clarix/ui';
import { useAuthStore } from '@clarix/auth-store';
import { useMe } from '@/core/api';
import { useActiveWorkspaceSlug } from '@/gateway/getWorkspaceSlug';
import { getAccessibleApps } from '@/gateway/accessibleApps';
import { AppBootSplash } from '@/gateway/AppBootSplash';
// One identity mark for every app-listing surface — the glyph on its brand
// tile, matching the workspace app shelf. See AppMark for why not the favicon.
import { AppMark } from '@/gateway/AppMark';

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]!.toUpperCase())
    .join('');
}

/**
 * Workspace root ("/"): instead of dropping the user straight into an app, show
 * a launcher so they pick where to go. If they can reach exactly one app there
 * is nothing to choose, so we enter it directly. Reads the same /me cache
 * WorkspaceShell populated (no extra request).
 *
 * One card per app, each carrying its own favicon, name and live blurb. The
 * grid collapses 3 → 2 → 1 column, so the same markup serves every breakpoint.
 */
export function AppLauncher() {
  const navigate = useNavigate();
  const slug = useActiveWorkspaceSlug();
  const me = useMe({ activeSlug: slug ?? undefined });
  const role = useAuthStore((s) => s.user?.workspace_role);

  const apps = useMemo(
    () => getAccessibleApps(me.data, { role, activeSlug: slug ?? undefined }),
    [me.data, role, slug],
  );

  const openApp = useCallback((app: AppDefinition) => navigate(`/${app.slug}`), [navigate]);

  if (me.isLoading || !me.data) return <AppBootSplash />;

  // Nothing to choose between — go straight in. Skip display-only apps: they
  // have no route, so auto-entering one would fall through the router catch-all
  // and, since it's the only accessible app, bounce back here in a redirect loop.
  if (apps.length === 1 && !apps[0]!.displayOnly)
    return <Navigate to={`/${apps[0]!.slug}`} replace />;

  const userName = me.data.user?.name || me.data.user?.email?.split('@')[0] || 'there';
  const workspaceName =
    me.data.workspaces.find((w) => w.slug === (slug ?? me.data?.active_workspace_slug))?.name ??
    'your workspace';

  return (
    <div className="bg-surface min-h-screen w-full overflow-y-auto px-5">
      <div className="mx-auto flex min-h-screen w-full max-w-4xl flex-col justify-center py-12">
        <header className="mb-7">
          {/* Workspace context sits above the greeting — the launcher is
              workspace-scoped, and the same user can hold several. */}
          <span className="mb-4 flex items-center gap-2">
            <span className="bg-muted text-foreground flex h-6 w-6 items-center justify-center rounded-md text-xs font-semibold">
              {initials(workspaceName)}
            </span>
            <span className="text-muted-foreground text-lg font-medium">{workspaceName}</span>
          </span>
          <h1 className="text-foreground text-4xl font-semibold">Welcome back, {userName}</h1>
          <p className="text-muted-foreground mt-1 text-xl">
            Choose an app to open in this workspace.
          </p>
        </header>

        {apps.length === 0 ? (
          <div className="border-border bg-background rounded-xl border p-8 text-center">
            <p className="text-foreground text-xl font-medium">No apps available yet</p>
            <p className="text-muted-foreground mt-1 text-xl">
              You don&apos;t have access to any apps in this workspace. Ask a workspace admin to
              grant access.
            </p>
          </div>
        ) : (
          <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {apps.map((app) => (
              <li key={app.id}>
                <AppCard app={app} onOpen={openApp} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/**
 * One app card: favicon, name, and the live blurb from the backend App registry
 * (overlaid onto the app definition by getAccessibleApps). The blurb is clamped
 * to two lines so cards in a row keep the same height whatever the copy.
 */
function AppCard({ app, onOpen }: { app: AppDefinition; onOpen: (app: AppDefinition) => void }) {
  // Display-only apps (backend-registered, no shell remote/route) render as a
  // non-navigable "coming soon" card — clicking through would hit the router
  // catch-all and open the wrong app.
  const openable = !app.displayOnly;
  // The accent is per-app runtime data (the registry can override it), so it
  // cannot be a static utility class. Root UI rule 1's sanctioned escape hatch
  // for exactly this: expose it as a CSS variable on the element and let the
  // classes below mix it — never a colour literal in the markup.
  const brandVar = app.brandColor
    ? ({ '--app-brand': app.brandColor } as CSSProperties)
    : undefined;
  return (
    <button
      type="button"
      disabled={!openable}
      aria-disabled={!openable}
      onClick={openable ? () => onOpen(app) : undefined}
      style={brandVar}
      className={cn(
        'border-border bg-background group flex h-full w-full flex-col gap-3 rounded-xl border p-4 text-left',
        'transition-colors',
        'focus-visible:ring-primary focus-visible:outline-none focus-visible:ring-2',
        !openable && 'cursor-not-allowed opacity-60',
        // No accent (registry app with no colour) → the original neutral hover.
        openable && !app.brandColor && 'hover:border-foreground/20 hover:bg-muted/40',
        openable &&
          app.brandColor && [
            // Light mode: a wash of the app's own mark colour, so the card the
            // pointer is on is tied to the icon the user is looking at. Kept
            // low (8%) — this is a hover hint behind body text, not a fill.
            'hover:bg-[color-mix(in_srgb,var(--app-brand)_8%,transparent)]',
            'hover:border-[color-mix(in_srgb,var(--app-brand)_30%,transparent)]',
            // Dark mode keeps the neutral hover: the same wash over a dark
            // surface reads as a muddy colour cast rather than an accent.
            'dark:hover:border-foreground/20 dark:hover:bg-muted/40',
          ],
      )}
    >
      <span className="flex w-full items-start justify-between gap-3">
        {/* AppMark IS the tile (glyph on the app's brand colour), so it needs
            no bordered wrapper — that would box a tile inside a box. */}
        <AppMark app={app} className="h-10 w-10" />
        {openable ? (
          <ArrowRight
            className="text-muted-foreground/50 group-hover:text-foreground mt-1 h-4 w-4 shrink-0 transition-colors"
            strokeWidth={1.5}
          />
        ) : (
          <span className="border-border text-muted-foreground mt-0.5 shrink-0 rounded-full border px-2 py-0.5 text-xs font-medium">
            Coming soon
          </span>
        )}
      </span>
      <span className="w-full">
        <span className="text-foreground block text-xl font-semibold">{app.name}</span>
        <span className="text-muted-foreground mt-1 line-clamp-2 block text-lg leading-relaxed">
          {app.description ??
            (openable ? `Open ${app.name}.` : `${app.name} isn't available here yet.`)}
        </span>
      </span>
    </button>
  );
}
